import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Badge Component – EPPREM 2.0 Healthcare Design System
 *
 * Badge text: sentence case (e.g., "Active", "Draft")
 * Uses design token status colors for light/dark support.
 */
const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-colors",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground",
        secondary:
          "border-primary/20 bg-primary-soft text-primary",
        destructive:
          "border-destructive/30 bg-destructive-soft text-destructive",
        outline: "text-foreground border-border",
        success:
          "border-success/30 bg-success-soft text-success dark:border-success/20",
        warning:
          "border-warning/30 bg-warning-soft text-warning dark:border-warning/20",
        info:
          "border-info/30 bg-info-soft text-info dark:border-info/20",
        muted:
          "border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-300",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
